import { createRelayEnvironment } from '@atlassian/relay-environment';
import { captureErrors } from '@atlassian/relay-errors';
import { useTenantData, useWorkspaceAri } from '@dev-agents/tenant-context';
import { useMemo } from 'react';
import { RelayEnvironmentProvider } from 'react-relay';
import type { Environment, GraphQLSingularResponse, RequestParameters } from 'relay-runtime';

const PRODUCT_NAME = 'Rovo Dev';
const SERVICE_NAME = 'dev-agents';
const TEAM_NAME = 'Jolt';

function validateResponse(request: RequestParameters, response: GraphQLSingularResponse): boolean {
	const errors = 'errors' in response ? response.errors : undefined;
	if (errors != null) {
		// @ts-expect-error errors path is string or number, platform captureErrors expects string only
		captureErrors(errors, { operationName: request.name });
	}
	return true;
}

const preInitAggRelayEnvironment = createRelayEnvironment({
	networkOptions: {
		isServer: false,
		getHeaders: () => ({
			'atl-client-name': SERVICE_NAME,
		}),
		validateResponse,
	},
});

const initializedAggRelayEnvironments = new Map<string, Environment>();

/**
 * atl-attribution header standard defined by AGG platform
 * https://hello.atlassian.net/wiki/spaces/AGG/blog/2025/10/30/6024467212/Blocking+requests+in+AGG+Staging+for+missing+metrics+attribution+headers
 */
const getAtlAttributionHeader = (workspaceAri: string, tenantAriOrCloudId: string) => {
	return {
		'atl-attribution': JSON.stringify({
			atlWorkspaceId: workspaceAri,
			tenantId: tenantAriOrCloudId,
			product: PRODUCT_NAME,
			service: SERVICE_NAME,
			team: TEAM_NAME,
		}),
	};
};

function initializeAggRelayEnvironment(
	workspaceAri: string,
	tenantAriOrCloudId: string,
): Environment {
	return createRelayEnvironment({
		networkOptions: {
			isServer: false,
			getHeaders: () => ({
				'atl-client-name': SERVICE_NAME,
				...getAtlAttributionHeader(workspaceAri, tenantAriOrCloudId),
			}),
			validateResponse,
		},
	});
}

export function getAggRelayEnvironment(
	workspaceAri: string,
	tenantAriOrCloudId: string,
): Environment {
	// Reuse the environment so route content and sidebar content update the same Relay store.
	const cacheKey = JSON.stringify({ workspaceAri, tenantAriOrCloudId });
	const cachedEnvironment = initializedAggRelayEnvironments.get(cacheKey);
	if (cachedEnvironment) {
		return cachedEnvironment;
	}

	const environment = initializeAggRelayEnvironment(workspaceAri, tenantAriOrCloudId);
	initializedAggRelayEnvironments.set(cacheKey, environment);
	return environment;
}

export const AggRelayEnvironmentProvider = ({ children }: { children: React.ReactNode }) => {
	const workspaceAri = useWorkspaceAri();
	const { tenantContexts } = useTenantData();
	const { cloudId } = tenantContexts?.[0] || {};

	const aggRelayEnvironment = useMemo(() => {
		if (workspaceAri && cloudId) {
			return getAggRelayEnvironment(workspaceAri, cloudId);
		}
		return preInitAggRelayEnvironment;
	}, [workspaceAri, cloudId]);

	return (
		<RelayEnvironmentProvider environment={aggRelayEnvironment}>
			{children}
		</RelayEnvironmentProvider>
	);
};
